import { Fragment, lazy, Suspense, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getTabStatus, TABS_LABELS, TABS_URLS } from "./helpers";
import { ApplicationFooter } from "./ApplicationFooter";
import { useHideMenusInFullscreen } from "./hooks/useHideMenusInFullscreen";
import { MenuButton, metricsBus } from "@/common/buses";
import { useAppStore } from "@/common/store/appStore";
import { useUserStore } from "@/common/store/userStore";
import { useLessNavigation } from "@/common/hooks/useLessNavigation";
import { isAuthenticated } from "@/common/services/Auth";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";

const ProfileMenu = lazy(() => import("@/components/profile-menu/ProfileMenu"));

const ApplicationFooterContainer = () => {
  const currentUserName = useUserStore((state) => state.currentUser?.username) || null;
  const isProfileMenuOpen = useAppStore((state) => state.isProfileMenuOpen);
  const toggleProfileMenu = useAppStore((state) => state.toggleProfileMenu);
  const hideAllMenus = useAppStore((state) => state.hideAllMenus);
  const { pathname } = useLocation();
  const { canShow } = useLessNavigation();
  const { navigateSignIn } = useAuthNavigate();
  const deviceType = useDeviceType();

  useHideMenusInFullscreen();

  const tabStatuses = useMemo(() => getTabStatus(pathname, currentUserName), [pathname, currentUserName]);

  const sendMetrics = useCallback((
    name: string,
    label: string,
    button: MenuButton,
  ) => {
    const device = deviceType === deviceTypeEnum.DESKTOP ? "desktop" : "mobile";

    metricsBus.emit("menuButtonClicked", { name, label, button, device, source_page: pathname });
  }, [deviceType, pathname]);

  const isTabActive = useCallback((tab: string) => tabStatuses[tab], [tabStatuses]);

  const onProfileClick = useCallback(async () => {
    const isProfile = currentUserName !== null;
    const label = isProfile ? TABS_LABELS.profile : TABS_LABELS.login;
    const button = isProfile ? "profile" : "login";

    sendMetrics(TABS_URLS.profile, label, button);

    if (!(await isAuthenticated())) {
      navigateSignIn();
      return;
    }

    toggleProfileMenu();
  }, [currentUserName, navigateSignIn, sendMetrics, toggleProfileMenu]);

  const onSendMetricAndCloseMenu = useCallback((
    tabName: TABS_URLS,
    label: TABS_LABELS,
    button: MenuButton,
  ) => {
    hideAllMenus();
    sendMetrics(tabName, label, button);
  }, [hideAllMenus, sendMetrics]);

  if (!canShow) {
    return null;
  }

  return (
    <Fragment>
      <ApplicationFooter
        currentUserName={currentUserName}
        isTabActive={isTabActive}
        onProfileClick={onProfileClick}
        onSendMetricAndCloseMenu={onSendMetricAndCloseMenu}
      />
      {isProfileMenuOpen && <Suspense><ProfileMenu /></Suspense>}
    </Fragment>
  );
};

export default ApplicationFooterContainer;
